import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { GridBackground } from "../EAKCArchitecture/components/GridBackground";

// Final post-synthesis pass: the completeness critic SPLICES back supported facts
// the first draft dropped (it appends — it does not rewrite), then a cited-only
// footer prune removes sources the answer never actually referenced.
const BASE_LINES = [
  "EAKC is a multi-tenant RAG knowledge base over codebases — chat UI + an MCP server.",
  "Ingestion: structural chunking → pgvector + hybrid search → grounded, cited answers.",
  "Retrieval runs intent → rewrite → hybrid search → graph → rerank → synthesis.",
];

const SPLICED_LINES = [
  "Artifact-tier quarantine drops test / eval / screenshot chunks before ranking.",
  "A CRAG gate triggers a broader corrective search only when the pool is weak.",
];

interface Src {
  num: number;
  file: string;
  cited: boolean;
}
const SOURCES: Src[] = [
  { num: 1, file: "map-reduce-response.ts", cited: true },
  { num: 2, file: "retrieval.ts", cited: true },
  { num: 3, file: "layout.tsx", cited: false },
  { num: 4, file: "graph-augmented-retrieval.ts", cited: true },
  { num: 5, file: "embeddings.ts", cited: false },
  { num: 6, file: "quality-gate.ts", cited: true },
];

export const SceneCompleteness: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleAppear = spring({ frame, fps, config: SPRING.snappy });
  const exitOpacity = interpolate(frame, [210, 240], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const spliceFired = frame > 80;
  const pruneFired = frame > 140;

  return (
    <AbsoluteFill style={{ opacity: exitOpacity }}>
      <GridBackground opacity={0.05} />

      {/* Title */}
      <div
        style={{
          position: "absolute",
          top: 44,
          left: "50%",
          transform: "translate(-50%, 0)",
          textAlign: "center",
          opacity: interpolate(titleAppear, [0, 1], [0, 1]),
        }}
      >
        <div style={{ fontSize: 40, fontFamily: FONTS.heading, fontWeight: 700, color: COLORS.textPrimary }}>
          Step 9:{" "}
          <span style={{ color: COLORS.accentGreen }}>Completeness &amp; Citation Prune</span>
        </div>
        <div style={{ fontSize: 18, fontFamily: FONTS.body, color: COLORS.textSecondary, marginTop: 8 }}>
          Splice back retrieved-but-omitted facts (append, don&rsquo;t rewrite) → keep only cited sources
        </div>
      </div>

      {/* Answer panel */}
      <div
        style={{
          position: "absolute",
          top: 160,
          left: "50%",
          transform: "translate(-50%, 0)",
          width: 1420,
          borderRadius: 16,
          background: "#0D1321",
          border: `1px solid ${COLORS.borderLight}`,
          padding: "22px 30px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <span style={{ fontSize: 20 }}>📋</span>
          <span style={{ fontFamily: FONTS.mono, fontSize: 14, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1.5 }}>
            Answer · completeness-checked
          </span>
          {spliceFired && (
            <span
              style={{
                marginLeft: "auto",
                fontFamily: FONTS.mono,
                fontSize: 13,
                color: COLORS.accentGreen,
                padding: "5px 12px",
                borderRadius: 8,
                background: `${COLORS.accentGreen}15`,
                border: `1px solid ${COLORS.accentGreen}35`,
                opacity: interpolate(spring({ frame: frame - 82, fps, config: SPRING.snappy }), [0, 1], [0, 1]),
              }}
            >
              critic spliced +2 facts
            </span>
          )}
        </div>

        {/* Base answer lines */}
        {BASE_LINES.map((line, i) => {
          const a = spring({ frame: frame - 12 - i * 8, fps, config: SPRING.snappy });
          return (
            <div
              key={i}
              style={{
                fontFamily: FONTS.body,
                fontSize: 19,
                color: COLORS.textPrimary,
                lineHeight: 1.5,
                marginBottom: 12,
                opacity: interpolate(a, [0, 1], [0, 1]),
              }}
            >
              • {line}
            </div>
          );
        })}

        {/* Spliced-in lines (highlighted green) */}
        {SPLICED_LINES.map((line, i) => {
          const a = spring({ frame: frame - 90 - i * 14, fps, config: SPRING.snappy });
          return (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                fontFamily: FONTS.body,
                fontSize: 19,
                color: COLORS.textPrimary,
                lineHeight: 1.5,
                marginBottom: 10,
                padding: "8px 14px",
                borderRadius: 10,
                background: `${COLORS.accentGreen}0F`,
                border: `1px solid ${COLORS.accentGreen}30`,
                opacity: interpolate(a, [0, 1], [0, 1]),
                transform: `translateX(${interpolate(a, [0, 1], [-16, 0])}px)`,
              }}
            >
              <span style={{ color: COLORS.accentGreen, fontWeight: 700 }}>＋</span>
              <span style={{ flex: 1 }}>{line}</span>
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  color: COLORS.accentGreen,
                  padding: "2px 8px",
                  borderRadius: 4,
                  background: `${COLORS.accentGreen}18`,
                }}
              >
                spliced
              </span>
            </div>
          );
        })}
      </div>

      {/* Sources footer — cited-only prune */}
      <div
        style={{
          position: "absolute",
          top: 620,
          left: "50%",
          transform: "translate(-50%, 0)",
          width: 1420,
        }}
      >
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 14,
            color: COLORS.textMuted,
            textTransform: "uppercase",
            letterSpacing: 1.5,
            marginBottom: 14,
          }}
        >
          Sources footer · cited-only prune
        </div>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          {SOURCES.map((s, i) => {
            const a = spring({ frame: frame - 40 - i * 8, fps, config: SPRING.snappy });
            const pruned = pruneFired && !s.cited;
            const c = s.cited ? COLORS.accentBlue : COLORS.accentRed;
            return (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 16px",
                  borderRadius: 10,
                  background: pruned ? `${COLORS.bgCard}60` : `${c}0D`,
                  border: `1px solid ${pruned ? COLORS.border : `${c}35`}`,
                  opacity: interpolate(a, [0, 1], [0, pruned ? 0.3 : 1]),
                  transform: `translateY(${interpolate(a, [0, 1], [14, 0])}px)`,
                }}
              >
                <span
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 6,
                    background: `${c}20`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: FONTS.mono,
                    fontSize: 12,
                    fontWeight: 700,
                    color: c,
                  }}
                >
                  {s.num}
                </span>
                <span
                  style={{
                    fontFamily: FONTS.mono,
                    fontSize: 14,
                    color: pruned ? COLORS.textMuted : COLORS.textSecondary,
                    textDecoration: pruned ? "line-through" : "none",
                  }}
                >
                  {s.file}
                </span>
                {pruned ? (
                  <span style={{ fontFamily: FONTS.mono, fontSize: 11, color: COLORS.accentRed, fontWeight: 700 }}>
                    pruned · uncited
                  </span>
                ) : (
                  <span style={{ fontSize: 13, color: COLORS.accentGreen }}>✓</span>
                )}
              </div>
            );
          })}
        </div>

        {pruneFired && (
          <div
            style={{
              marginTop: 22,
              fontFamily: FONTS.mono,
              fontSize: 15,
              color: COLORS.textMuted,
              opacity: interpolate(spring({ frame: frame - 150, fps, config: SPRING.snappy }), [0, 1], [0, 1]),
            }}
          >
            Footer lists only what the answer actually cited —{" "}
            <span style={{ color: COLORS.accentGreen, fontWeight: 700 }}>complete, grounded, no phantom sources</span>
          </div>
        )}
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
